import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import Link from "~/components/Link";
import { useUnpaidOrders } from "~/components/providers/UnpaidOrdersContext";
import { trackEvent } from "~/connectors/analytics.connector";
import { AnalyticsEvents } from "~/connectors/analyticsEvents";
import { formatAgeGenderNameDescription } from "~/lib/formatAgeGenderNameDescription";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grow from "@mui/material/Grow";
import ShoppingCart from "@mui/icons-material/ShoppingCart";

export default function UnpaidOrdersPopup() {
  const pathname = usePathname();
  const { unpaidOrders, visibleOrders, dismissAllOrders } = useUnpaidOrders();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(visibleOrders.length > 0);
  }, [visibleOrders]);

  // Track when popup becomes visible
  useEffect(() => {
    if (isVisible) {
      trackEvent(AnalyticsEvents.ORDER_UNPAID_POPUP_SHOWN);
    }
  }, [isVisible]);

  const handleDismissAll = () => {
    dismissAllOrders();
    setIsVisible(false);
  };

  // Check if we're on an unpaid order page
  const isOnUnpaidOrderPage = (() => {
    const match = pathname.match(/^\/dashboard\/comenzi\/(\d+)/);
    if (!match) return false;
    const orderId = parseInt(match[1], 10);
    return visibleOrders.some((order) => order.id === orderId);
  })();

  if (!isVisible || visibleOrders.length === 0 || isOnUnpaidOrderPage) {
    return null;
  }

  const isMultipleOrders = visibleOrders.length > 1;

  return (
    <Grow in={isVisible}>
      <Card
        sx={{
          position: "fixed",
          top: 80,
          right: 20,
          minWidth: 320,
          maxWidth: 420,
          zIndex: 1300,
          boxShadow: 8,
          borderRadius: 3,
          overflow: "hidden",
          "&:hover": {
            boxShadow: 12,
          },
          transition: "all 0.3s ease-in-out",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {isMultipleOrders ? (
            // Multiple orders - show summary
            (<>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <ShoppingCart color="warning" />
                <Typography
                  variant="h6"
                  sx={{ color: "warning.main", fontWeight: 500 }}
                >
                  {unpaidOrders?.pagination.total} comenzi așteaptă plată
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: 3,
                  lineHeight: 1.6
                }}>
                Comenzile tale stau cumințele și te așteaptă. Finalizează plata
                și ne apucăm de treabă.
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Link
                  href="/dashboard/comenzi"
                  onClick={handleDismissAll}
                  button
                  variant="contained"
                  fullWidth
                >
                  Vezi comenzile
                </Link>
                <Button variant="outlined" onClick={handleDismissAll} fullWidth>
                  Nu acum
                </Button>
              </Box>
            </>)
          ) : (
            // Single order - show details
            (<>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <ShoppingCart color="warning" />
                <Typography
                  variant="h6"
                  sx={{ color: "warning.main", fontWeight: 500 }}
                >
                  Comandă neplătită
                </Typography>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {formatAgeGenderNameDescription(
                    visibleOrders[0].formData?.age,
                    visibleOrders[0].formData?.gender,
                    visibleOrders[0].formData?.childName,
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.6
                  }}>
                  {visibleOrders[0].formData?.story
                    ? `${visibleOrders[0].formData.story.substring(0, 120)}${visibleOrders[0].formData.story.length > 120 ? "..." : ""}`
                    : "Fără poveste specificată"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Link
                  href={`/dashboard/comenzi/${visibleOrders[0].id}`}
                  button
                  trackable={
                    AnalyticsEvents.ORDER_UNPAID_POPUP_RESUME_PAYMENT_CLICKED
                  }
                  variant="contained"
                  fullWidth
                >
                  Reia plata
                </Link>
                <Button variant="outlined" onClick={handleDismissAll} fullWidth>
                  Nu acum
                </Button>
              </Box>
            </>)
          )}
        </CardContent>
      </Card>
    </Grow>
  );
}
